/**
 * CDP SDK — consent posture resolution + signal bridges.
 *
 * Implements the per-site consent MODE on top of the existing category model
 * (functional / analytics / marketing / personalization in `consent.ts`):
 *
 *  - `anonymous-until-consent` (default): before an explicit choice, run a LITE
 *    posture — analytics granted but de-identified (no persistent ID, IP
 *    truncated server-side, events flagged `anonymous:true`). No mode computes a
 *    device fingerprint since 20.10 (ЛО-13).
 *    On consent, the SDK upgrades to full identified tracking.
 *  - `strict`: nothing non-functional until the visitor opts in.
 *  - `cmp-only`: defer entirely to the site's CMP / cdp.consent().
 *  - `implied`: everything granted on init (legacy requireConsent:false).
 *
 * Plus two read-only signal bridges that only ever RESTRICT: Global Privacy
 * Control / Do-Not-Track, and an IAB TCF v2.2 CMP (window.__tcfapi).
 */

import type { ConsentPreferences, SiteProfile } from './types';
import { log } from './utils';

const NONE: ConsentPreferences = {
  functional: true,
  analytics: false,
  marketing: false,
  personalization: false,
};

const ALL: ConsentPreferences = {
  functional: true,
  analytics: true,
  marketing: true,
  personalization: true,
};

// Lite = analytics flows but de-identified; marketing/personalization withheld.
const LITE: ConsentPreferences = {
  functional: true,
  analytics: true,
  marketing: false,
  personalization: false,
};

export interface InitialConsent {
  /** Effective consent categories to seed the SDK with. */
  consent: ConsentPreferences;
  /** Lite (anonymous) posture: de-identify events, no persistent ID. */
  lite: boolean;
}

/** Detect a legal opt-out signal: Global Privacy Control or Do-Not-Track. */
export function detectGpcDnt(): boolean {
  try {
    const nav = navigator as unknown as Record<string, unknown>;
    if (nav.globalPrivacyControl === true) return true;
    const dnt =
      (nav.doNotTrack as string) ||
      ((window as unknown as Record<string, unknown>).doNotTrack as string) ||
      (nav.msDoNotTrack as string);
    return dnt === '1' || dnt === 'yes';
  } catch {
    return false;
  }
}


/**
 * Resolve the consent posture at init time from the site's mode, any stored
 * choice, and the GPC/DNT signal. A stored explicit choice always wins; GPC/DNT
 * forces a private posture; otherwise the mode decides.
 */
export function resolveInitialConsent(
  profile: SiteProfile,
  stored: ConsentPreferences | null
): InitialConsent {
  // Default posture is FULL tracking (implied) so profiles are created normally:
  // a persistent, per-visitor identity with full resolution + identify()
  // stitching, exactly as before the Site Profile work — no per-session
  // fragmentation. Privacy postures (anonymous-until-consent / strict / cmp-only)
  // are OPT-IN per site via the Site Profile's consent.mode. GDPR is still served
  // by always-on IP anonymization + GPC/DNT honoring + the consent API + the
  // ability to set a stricter per-site mode.
  const mode = profile.consent?.mode || 'implied';
  const honorGpcDnt = profile.consent?.honorGpcDnt !== false;

  // A stored decision wins — a decline included (07.10: `stored` is passed only
  // when the browser holds a decision, see hasStoredConsent(); an all-false record
  // used to count as "no choice", so a visitor who declined got the default
  // posture again on every page until the CMP re-applied the "no").
  if (stored) {
    return { consent: { ...NONE, ...stored }, lite: false };
  }

  if (honorGpcDnt && detectGpcDnt()) {
    log('Consent: GPC/DNT present — withholding non-functional tracking');
    // Opted out: nothing non-functional flows (canSendEvent blocks analytics too).
    return { consent: { ...NONE }, lite: false };
  }

  switch (mode) {
    case 'implied':
      return { consent: { ...ALL }, lite: false };
    case 'strict':
    case 'cmp-only':
      return { consent: { ...NONE }, lite: false };
    case 'anonymous-until-consent':
    default:
      return { consent: { ...LITE }, lite: true };
  }
}

// =============================================================================
// IAB TCF v2.2 BRIDGE
// =============================================================================

interface TcfData {
  gdprApplies?: boolean;
  purpose?: { consents?: Record<string, boolean> };
  eventStatus?: string;
}

/** Map TCF purpose consents (v2.2) to our four categories (best-effort). */
function tcfToConsent(data: TcfData): ConsentPreferences {
  const p = data.purpose?.consents || {};
  // P1 store/access; P7/8/9/10 measure performance & develop products → analytics.
  const analytics = !!(p['1'] && (p['7'] || p['8'] || p['9'] || p['10']));
  // P2/3/4 select/personalise ads → marketing.
  const marketing = !!(p['2'] || p['3'] || p['4']);
  // P5/6 personalise content → personalization.
  const personalization = !!(p['5'] || p['6']);
  return { functional: true, analytics, marketing, personalization };
}

type TcfApi = (
  cmd: string,
  version: number,
  cb: (data: TcfData, success: boolean) => void
) => void;

/** How long events wait for a TCF CMP's answer before the mode decides alone (LA-06). */
export const TCF_WAIT_MS = 3000;

/**
 * Whether this page's events wait for the TCF CMP's answer before they go out.
 *
 * LA-06 (monfri docs/legal/audits/LEGAL-FULL-AUDIT-2026-10-20.md, decision ЛО-13,
 * found 07.10 in LAUNCH-READINESS §6): an `implied` site with a TCF CMP — dir.bg,
 * 24chasa — sent the page view of a visitor who had said "no" in the CMP on every
 * page, because the implied posture grants everything at init and the CMP's answer
 * arrives a moment later. A stored "no" does not help there: an implied site reads
 * an all-false record as "no choice" (asksForConsent in core/index.ts), and changing
 * that is the mode choice the site owner makes, not this fix.
 *
 * Only `implied`: the other modes send nothing identifying before a decision
 * anyway (anonymous-until-consent sends its de-identified events by design).
 */
export function waitsForTcf(profile: SiteProfile): boolean {
  if (profile.consent?.tcf === false) return false;
  if ((profile.consent?.mode || 'implied') !== 'implied') return false;
  try {
    return typeof (window as unknown as { __tcfapi?: TcfApi }).__tcfapi === 'function';
  } catch {
    return false;
  }
}

/**
 * If an IAB TCF v2.2 CMP is present and enabled in the profile, subscribe to it
 * and forward consent changes to `onChange`. Returns a disposer. The bridge only
 * grants categories the CMP reports — it never overrides a stricter SDK state.
 *
 * `onSettled` is called after every answer that ends the wait for the CMP (LA-06):
 * the CMP says GDPR does not apply, or it reports the visitor's choice (`tcloaded`
 * for a stored one, `useractioncomplete` for a new one) — always AFTER `onChange`
 * has applied that choice, so a refusal is in force before a held event is sent.
 * `cmpuishown` is not an answer: the visitor has not chosen yet, and the wait runs
 * on until the choice or the timeout.
 */
export function initTcfBridge(
  profile: SiteProfile,
  onChange: (consent: ConsentPreferences) => void,
  onSettled?: () => void
): () => void {
  if (profile.consent?.tcf === false) return () => {};
  const tcfapi = (window as unknown as { __tcfapi?: TcfApi }).__tcfapi;
  if (typeof tcfapi !== 'function') return () => {};

  const handler = (data: TcfData, success: boolean) => {
    if (!success || !data) return;
    // gdprApplies === false → CMP says GDPR not in scope; leave SDK posture as is.
    if (data.gdprApplies === false) {
      onSettled?.();
      return;
    }
    if (
      data.eventStatus === 'useractioncomplete' ||
      data.eventStatus === 'tcloaded'
    ) {
      log('Consent: TCF signal received', data.eventStatus);
      onChange(tcfToConsent(data));
      onSettled?.();
    }
  };

  try {
    tcfapi('addEventListener', 2, handler);
  } catch {
    /* CMP not ready / unsupported — ignore */
  }

  return () => {
    try {
      tcfapi('removeEventListener', 2, () => {});
    } catch {
      /* ignore */
    }
  };
}
